// Tank 2D Battle Arena Client (Top-down Maze War)
import { sounds } from '../sound.js';

export class TankGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.tanks = {};
    this.bullets = [];
    this.obstacles = [];
    this.input = { forward: false, backward: false, left: false, right: false };

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="tank-wrapper">
        <div class="tank-header">
          <div class="tank-meta">
            <h2>💥 TANK 2D BATTLE ARENA</h2>
            <div class="tank-sub">WASD to Drive • Mouse to Aim Turret • Click to Shoot (Bouncing Shells!)</div>
          </div>
          <div id="tank-hp-badge" class="tank-badge">HP: 100%</div>
        </div>

        <div class="tank-canvas-box">
          <canvas id="tank-canvas" width="800" height="520"></canvas>
        </div>

        <!-- Virtual Touch D-Pad for Mobile Players -->
        <div class="mobile-touch-bar">
          <div class="touch-dpad-cluster">
            <button id="touch-tank-left" class="btn-virtual-touch">◀</button>
            <button id="touch-tank-right" class="btn-virtual-touch">▶</button>
            <button id="touch-tank-up" class="btn-virtual-touch">▲</button>
            <button id="touch-tank-down" class="btn-virtual-touch">▼</button>
          </div>
          <div class="touch-action-cluster">
            <button id="touch-tank-fire" class="btn-virtual-touch btn-virtual-action">💥 FIRE</button>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('tank-canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  setupControls() {
    const handleKey = (e, isDown) => {
      let changed = false;
      const key = e.key ? e.key.toLowerCase() : '';
      if (e.code === 'KeyW' || key === 'w' || e.code === 'ArrowUp') { this.input.forward = isDown; changed = true; }
      if (e.code === 'KeyS' || key === 's' || e.code === 'ArrowDown') { this.input.backward = isDown; changed = true; }
      if (e.code === 'KeyA' || key === 'a' || e.code === 'ArrowLeft') { this.input.left = isDown; changed = true; }
      if (e.code === 'KeyD' || key === 'd' || e.code === 'ArrowRight') { this.input.right = isDown; changed = true; }

      if (changed) {
        this.socket.emit('tank_input', this.input);
      }
    };

    const attachTouchBtn = (btnId, keyName) => {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      const setPressed = (pressed) => {
        if (this.input[keyName] !== pressed) {
          this.input[keyName] = pressed;
          this.socket.emit('tank_input', this.input);
        }
      };
      btn.addEventListener('touchstart', (e) => { e.preventDefault(); setPressed(true); }, { passive: false });
      btn.addEventListener('touchend', (e) => { e.preventDefault(); setPressed(false); }, { passive: false });
      btn.addEventListener('mousedown', () => setPressed(true));
      btn.addEventListener('mouseup', () => setPressed(false));
      btn.addEventListener('mouseleave', () => setPressed(false));
    };

    attachTouchBtn('touch-tank-left', 'left');
    attachTouchBtn('touch-tank-right', 'right');
    attachTouchBtn('touch-tank-up', 'forward');
    attachTouchBtn('touch-tank-down', 'backward');

    document.getElementById('touch-tank-fire')?.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.socket.emit('tank_fire');
    }, { passive: false });
    document.getElementById('touch-tank-fire')?.addEventListener('click', () => {
      this.socket.emit('tank_fire');
    });

    this.keyDownHandler = (e) => handleKey(e, true);
    this.keyUpHandler = (e) => handleKey(e, false);

    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);

    this.mouseMoveHandler = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const clickX = (e.clientX - rect.left) * (this.canvas.width / rect.width);
      const clickY = (e.clientY - rect.top) * (this.canvas.height / rect.height);

      const myTank = this.tanks[this.socket.id];
      if (myTank && myTank.isAlive) {
        const angle = Math.atan2(clickY - myTank.y, clickX - myTank.x);
        this.socket.emit('tank_turret', { angle });
      }
    };

    this.clickHandler = () => {
      const myTank = this.tanks[this.socket.id];
      if (myTank && myTank.isAlive) {
        this.socket.emit('tank_fire');
        sounds.playPop();
      }
    };

    this.canvas.addEventListener('mousemove', this.mouseMoveHandler);
    this.canvas.addEventListener('click', this.clickHandler);
  }

  setupSocketEvents() {
    this.socket.on('tank_state', (data) => {
      this.tanks = data.tanks;
      this.bullets = data.bullets;
      this.obstacles = data.obstacles;

      const myTank = this.tanks[this.socket.id];
      const hpBadge = document.getElementById('tank-hp-badge');
      if (hpBadge && myTank) {
        hpBadge.innerText = myTank.isAlive ? `HP: ${myTank.hp}%` : 'DESTROYED 💀';
        hpBadge.style.color = myTank.isAlive ? '#22c55e' : '#ef4444';
      }

      this.draw();
    });

    this.socket.on('tank_hit', (data) => {
      if (data.victimId === this.socket.id) {
        sounds.playExplosion();
      }
    });

    this.socket.on('tank_gameOver', (data) => {
      sounds.playVictory();
      alert(`🏆 TANK ARENA CHAMPION: ${data.winner?.name || 'Player'}!`);
    });
  }

  draw() {
    if (!this.ctx) return;
    this.ctx.fillStyle = '#0f172a';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Obstacles
    this.ctx.fillStyle = '#334155';
    this.obstacles.forEach((ob) => {
      this.ctx.fillRect(ob.x, ob.y, ob.w, ob.h);
      this.ctx.strokeStyle = '#475569';
      this.ctx.strokeRect(ob.x, ob.y, ob.w, ob.h);
    });

    // Bullets
    this.ctx.fillStyle = '#fbbf24';
    this.ctx.shadowColor = '#fbbf24';
    this.ctx.shadowBlur = 8;
    this.bullets.forEach((b) => {
      this.ctx.beginPath();
      this.ctx.arc(b.x, b.y, 4, 0, Math.PI * 2);
      this.ctx.fill();
    });
    this.ctx.shadowBlur = 0;

    // Tanks
    for (const id in this.tanks) {
      const t = this.tanks[id];
      if (!t.isAlive) continue;

      this.ctx.save();
      this.ctx.translate(t.x, t.y);

      // Tank Body (Chassis)
      this.ctx.save();
      this.ctx.rotate(t.angle);
      this.ctx.fillStyle = t.color;
      this.ctx.fillRect(-16, -12, 32, 24);
      // Treads
      this.ctx.fillStyle = '#1e293b';
      this.ctx.fillRect(-18, -15, 36, 4);
      this.ctx.fillRect(-18, 11, 36, 4);
      this.ctx.restore();

      // Turret Barrel
      this.ctx.save();
      this.ctx.rotate(t.turretAngle);
      this.ctx.fillStyle = '#64748b';
      this.ctx.fillRect(0, -3, 20, 6);
      this.ctx.fillStyle = t.color;
      this.ctx.beginPath();
      this.ctx.arc(0, 0, 8, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.restore();

      this.ctx.restore();

      // Tank Name
      this.ctx.fillStyle = '#ffffff';
      this.ctx.font = 'bold 11px Rajdhani, sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(`${t.name} (${t.hp}%)`, t.x, t.y - 20);
    }
  }

  destroy() {
    if (this.keyDownHandler) window.removeEventListener('keydown', this.keyDownHandler);
    if (this.keyUpHandler) window.removeEventListener('keyup', this.keyUpHandler);
    if (this.mouseMoveHandler) this.canvas?.removeEventListener('mousemove', this.mouseMoveHandler);
    if (this.clickHandler) this.canvas?.removeEventListener('click', this.clickHandler);

    this.socket.off('tank_state');
    this.socket.off('tank_hit');
    this.socket.off('tank_gameOver');
  }
}
